import { inject, Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { concatLatestFrom } from '@ngrx/operators';
import { Action, Store } from '@ngrx/store';
import {
  concatMap,
  exhaustMap,
  filter,
  forkJoin,
  map,
  mergeMap,
  MonoTypeOperatorFunction,
  Observable,
  of,
  OperatorFunction,
  switchMap,
  tap,
  toArray,
} from 'rxjs';
import { Option, Result } from '@sapphire/result';
import { match } from 'ts-pattern';
import { Nullable } from '@ps/utils';

import { EffectsBase, ReferenceDataFacade } from '@ps/core-data-access';
import { AuthSelectorsFacade } from '@ps/auth-data-access';
import { ToastService } from '@ps/core-overlay';
import { ProjectService, SurveyService } from '@ps/survey-data-access';
import {
  localeLabel,
  Survey,
  MC_GROUPED_MESSAGES,
  MIN_GROUPED_MC_LOCALES,
  MC_MESSAGES,
  MC_TRADITIONAL_MESSAGES,
  McTraditionalSurveyPayloadInput,
  ProjectUpdatePayloadInput,
  SURVEY_CLASSIFICATION_KIND,
  SurveyClassification,
  SurveyKind,
  SurveyLocaleData,
  SurveyPricingSnapshot,
} from '@ps/survey-utils';

import {
  selectFeasibilityCpi,
  selectInsightsChildren,
  selectPricing,
} from '../insights';
import { selectQualificationsRecord } from '../reference/survey-creation-reference.selectors';

import {
  SurveyCreationMcActions,
  SurveyCreationMcApiActions,
} from './survey-creation-audience.actions';
import {
  selectAdvancedFeasibilityFilters,
  selectAudienceChildren,
} from './survey-creation-audience.selectors';
import {
  selectCoreChildren,
  selectKind,
  selectSurvey,
} from '../core/survey-creation-core.selectors';
import {
  addedLocaleInput,
  McCreatedSurvey,
  mcParentSurvey,
  McTraditionalSurveyOutcome,
  sameCountrySiblingIds,
  sameCountrySurveyIds,
} from './utils/mc-traditional-save.helper';
import {
  buildPreDraftChildDrafts,
  PreDraftChildDraft,
  PreDraftChildDraftsInput,
} from './utils/pre-draft-save.helper';
import {
  firstChildEntry,
  participatingChildCount,
} from './utils/audience-children.helper';

import { ChildSwitchGuardService } from '../../services/child-switch/child-switch-guard.service';
import { ProjectUpdateInputBuilder } from '../../services/save/project-update-input.builder';
import { appendProjectChild } from './utils/project-save.helper';
import { SurveyChildEntry } from '../survey-creation-child-registry.helper';
import { SurveyChildKey } from '../survey-creation-shared-state.model';
import { SurveyChildRegistryState } from '../survey-creation-state.model';
import { DEFAULT_CORE_CHILD_STATE } from '../survey-creation-state.constants';

/**
 * The row the endpoint created for a locale just added. A saved project lists
 * every locale it holds with that locale's row id, so the entry matching the
 * one added is the new row. Locale-specific, and so specific to grouped MC.
 */
function addedChildSurveyId(
  project: Survey,
  locale: SurveyLocaleData,
): Option<number> {
  return Option.from(
    project.mc?.surveys.find(
      ({ countryCode, languageShortCode }) =>
        countryCode === locale.countryCode &&
        languageShortCode === locale.languageShortCode,
    ),
  ).map(({ surveyId }) => surveyId);
}

@Injectable()
export class SurveyCreationMcSaveEffects extends EffectsBase {
  private readonly _actions$ = inject(Actions);
  private readonly _store = inject(Store);
  private readonly _surveyService = inject(SurveyService);
  private readonly _projectService = inject(ProjectService);
  private readonly _authSelectors = inject(AuthSelectorsFacade);
  private readonly _toastService = inject(ToastService);
  private readonly _referenceData = inject(ReferenceDataFacade);
  private readonly _childSwitchGuard = inject(ChildSwitchGuardService);
  private readonly _projectUpdateInput = inject(ProjectUpdateInputBuilder);

  public readonly createMcTraditionalSurveys$ = createEffect(() =>
    this._actions$.pipe(
      ofType(SurveyCreationMcActions.createMcTraditionalSurveys),
      this._withSelectedDrafts(),
      exhaustMap((drafts) => this._createMcTraditionalSurveys(drafts)),
    ),
  );

  public readonly createMcGroupedProject$ = createEffect(() =>
    this._actions$.pipe(
      ofType(SurveyCreationMcActions.createMcGroupedProject),
      this._withSelectedDrafts(),
      exhaustMap((drafts) =>
        this._projectService
          .createMcGroupedProject({
            children: drafts.map(({ input }) => input),
          })
          .pipe(
            map((survey) => {
              this._toastService.success(MC_GROUPED_MESSAGES.SAVED, {
                keepOpen: false,
              });
              return SurveyCreationMcApiActions.createMcGroupedProjectSuccess({
                survey,
              });
            }),
            this.psCatchError(
              (error) =>
                of(
                  SurveyCreationMcApiActions.createMcGroupedProjectFailure({
                    error: error.message,
                  }),
                ),
              { notify: true },
            ),
          ),
      ),
    ),
  );

  public readonly addSurveyChild$ = createEffect(() =>
    this._actions$.pipe(
      ofType(SurveyCreationMcActions.addSurveyChild),
      concatLatestFrom(() => [
        this._store.select(selectSurvey),
        this._store.select(selectAdvancedFeasibilityFilters),
        this._store.select(selectKind),
      ]),
      filter(([, survey, advancedFilters]) =>
        this._childSwitchGuard.allowsAdd(survey, advancedFilters),
      ),
      map(([action, , , kind]) => [action, kind] as const),
      concatMap(([action, kind]) => this._addSurveyChild(kind, action)),
    ),
  );

  public readonly deleteSurveyChild$ = createEffect(() =>
    this._actions$.pipe(
      ofType(SurveyCreationMcActions.deleteSurveyChild),
      concatLatestFrom(() => [
        this._store.select(selectKind),
        this._store.select(selectCoreChildren),
      ]),
      concatMap(([{ surveyId }, kind, coreChildren]) =>
        this._deleteSurveyChild(kind, surveyId, coreChildren),
      ),
    ),
  );

  private _addSurveyChild(
    kind: Nullable<SurveyClassification>,
    action: ReturnType<typeof SurveyCreationMcActions.addSurveyChild>,
  ): Observable<Action> {
    return match(kind?.kind)
      .with(SURVEY_CLASSIFICATION_KIND.GROUP_MC, () =>
        this._addGroupedMcChild(action),
      )
      .with(SURVEY_CLASSIFICATION_KIND.MC, () =>
        this._addTraditionalChild(action),
      )
      .otherwise(() =>
        this._rejectAddedLocale(MC_MESSAGES.LOCALE_NOT_SAVEABLE),
      );
  }

  private _addTraditionalChild(
    action: ReturnType<typeof SurveyCreationMcActions.addSurveyChild>,
  ): Observable<Action> {
    const { countryCode, languageShortCode } = action;

    return of(action).pipe(
      this._withDraftContext(),
      concatMap(([, draftContext]) =>
        draftContext.match({
          ok: (context) =>
            this._addMcTraditionalChild(context, {
              countryCode,
              languageShortCode,
            }),
          err: (error) => this._rejectAddedLocale(error),
        }),
      ),
    );
  }

  // A project writes every child in one PUT, so a new one is added by sending
  // the project's own payload with that child appended — no `survey_id` on it
  // is what tells the endpoint to create its row.
  private _addGroupedMcChild(locale: SurveyLocaleData): Observable<Action> {
    return this._projectUpdateInput
      .build({
        noChildrenSelected: MC_MESSAGES.NO_LOCALES_SELECTED,
        childNotSaveable: MC_MESSAGES.LOCALE_NOT_SAVEABLE,
      })
      .match({
        some: (built) =>
          appendProjectChild(built, locale).match({
            ok: (payload) =>
              this._writeAddedChild(built.project.id, payload, locale),
            err: (error) => this._rejectAddedLocale(error),
          }),
        none: () => this._rejectAddedLocale(MC_MESSAGES.LOCALE_NOT_SAVEABLE),
      });
  }

  private _writeAddedChild(
    projectId: number,
    payload: ProjectUpdatePayloadInput,
    locale: SurveyLocaleData,
  ): Observable<Action> {
    return this._projectService.updateMcGroupedProject(projectId, payload).pipe(
      concatMap((project) => this._loadAddedChild(project, locale)),
      this._catchAddFailure(),
    );
  }

  // The PUT answers with the project, so the children are read back to pick up
  // the row it created for the locale just added. The project must already name
  // that row — without its id there is nothing to match in the response, so the
  // read is skipped rather than issued and then failed on.
  //
  // No catch of its own: the only caller runs this inside its concatMap, so a
  // read failure surfaces through that one `_catchAddFailure`.
  private _loadAddedChild(
    project: Survey,
    locale: SurveyLocaleData,
  ): Observable<Action> {
    const childFilter = SurveyKind.childFilter(project).unwrapOr({
      projectId: project.id,
    });

    return addedChildSurveyId(project, locale).match({
      some: (surveyId) =>
        this._surveyService.getChildSurveys(childFilter).pipe(
          concatMap((surveys) =>
            Option.from(surveys.find(({ id }) => id === surveyId)).match({
              some: (survey) => {
                this._toastService.success(MC_GROUPED_MESSAGES.LOCALE_ADDED, {
                  keepOpen: false,
                });

                return of(
                  SurveyCreationMcApiActions.addSurveyChildSuccess({ survey }),
                );
              },
              none: () =>
                this._rejectAddedLocale(MC_MESSAGES.LOCALE_NOT_SAVEABLE),
            }),
          ),
        ),
      none: () => this._rejectAddedLocale(MC_MESSAGES.LOCALE_NOT_SAVEABLE),
    });
  }

  private _catchAddFailure(): MonoTypeOperatorFunction<Action> {
    return this.psCatchError(
      (error) =>
        of(
          SurveyCreationMcApiActions.addSurveyChildFailure({
            error: error.message,
          }),
        ),
      { notify: true },
    );
  }

  private _addMcTraditionalChild(
    context: PreDraftChildDraftsInput,
    locale: SurveyLocaleData,
  ): Observable<Action> {
    return firstChildEntry(context.children)
      .match({
        some: (entry) => addedLocaleInput(entry, context, locale),
        none: () => Result.err(MC_MESSAGES.LOCALE_NOT_SAVEABLE),
      })
      .match({
        ok: (input) => this._createAddedLocale(input, context),
        err: (error) => this._rejectAddedLocale(error),
      });
  }

  private _createAddedLocale(
    input: McTraditionalSurveyPayloadInput,
    context: PreDraftChildDraftsInput,
  ): Observable<Action> {
    return this._surveyService.createMcTraditionalSurvey(input).pipe(
      switchMap((survey) => this._announceAddedLocale(survey, context)),
      this.psCatchError(
        (error) =>
          of(
            SurveyCreationMcApiActions.addSurveyChildFailure({
              error: error.message,
            }),
          ),
        { notify: true },
      ),
    );
  }

  private _rejectAddedLocale(error: string): Observable<Action> {
    this._toastService.error(error);

    return of(SurveyCreationMcApiActions.addSurveyChildFailure({ error }));
  }

  private _announceAddedLocale(
    survey: Survey,
    context: PreDraftChildDraftsInput,
  ): Observable<Action> {
    this._toastService.success(MC_TRADITIONAL_MESSAGES.LOCALE_ADDED, {
      keepOpen: false,
    });

    return this._excludeSameCountrySiblings(
      survey.id,
      sameCountrySurveyIds(context.children, survey),
    ).pipe(
      map(() => SurveyCreationMcApiActions.addSurveyChildSuccess({ survey })),
    );
  }

  private _deleteSurveyChild(
    kind: Nullable<SurveyClassification>,
    surveyId: number,
    coreChildren: Record<SurveyChildKey, SurveyChildRegistryState>,
  ): Observable<Action> {
    if (
      kind?.kind !== SURVEY_CLASSIFICATION_KIND.MC &&
      kind?.kind !== SURVEY_CLASSIFICATION_KIND.GROUP_MC
    ) {
      return this._rejectDeletedLocale(MC_MESSAGES.LOCALE_NOT_SAVEABLE);
    }

    const isGroupedMC = kind.kind === SURVEY_CLASSIFICATION_KIND.GROUP_MC;

    if (
      isGroupedMC &&
      participatingChildCount(coreChildren) <= MIN_GROUPED_MC_LOCALES
    ) {
      return this._rejectDeletedLocale(
        MC_GROUPED_MESSAGES.MIN_LOCALES_REQUIRED,
      );
    }

    return this._surveyService.deleteSurvey(surveyId).pipe(
      map(() => {
        this._toastService.success(
          isGroupedMC
            ? MC_GROUPED_MESSAGES.LOCALE_DELETED
            : MC_MESSAGES.LOCALE_DELETED,
          { keepOpen: false },
        );

        return SurveyCreationMcApiActions.deleteSurveyChildSuccess({
          surveyId,
        });
      }),
      this.psCatchError(
        (error) =>
          of(
            SurveyCreationMcApiActions.deleteSurveyChildFailure({
              error: error.message,
            }),
          ),
        { notify: true },
      ),
    );
  }

  private _rejectDeletedLocale(error: string): Observable<Action> {
    this._toastService.error(error);

    return of(SurveyCreationMcApiActions.deleteSurveyChildFailure({ error }));
  }

  private _createMcTraditionalSurveys(
    drafts: PreDraftChildDraft[],
  ): Observable<Action> {
    const mcUniqueId = crypto.randomUUID();

    return forkJoin(
      drafts.map((draft, index) =>
        this._createMcTraditionalSurvey(draft, mcUniqueId, index === 0),
      ),
    ).pipe(
      concatMap((outcomes) => this._announceMcTraditionalSurveys(outcomes)),
    );
  }

  private _createMcTraditionalSurvey(
    { key, input }: PreDraftChildDraft,
    mcUniqueId: string,
    isParent: boolean,
  ): Observable<McTraditionalSurveyOutcome> {
    return this._surveyService
      .createMcTraditionalSurvey({ ...input, mcUniqueId, isParent })
      .pipe(
        map(
          (survey): McTraditionalSurveyOutcome => ({
            status: 'created',
            locale: String(key),
            isParent,
            survey,
          }),
        ),
        this.psCatchError(
          () =>
            of<McTraditionalSurveyOutcome>({
              status: 'rejected',
              locale: String(key),
            }),
          { metadata: { notify: false } },
        ),
      );
  }

  private _announceMcTraditionalSurveys(
    outcomes: McTraditionalSurveyOutcome[],
  ): Observable<Action> {
    const rejected = outcomes.filter(
      (outcome) => outcome.status === 'rejected',
    );
    const created = outcomes.filter(
      (outcome): outcome is McCreatedSurvey => outcome.status === 'created',
    );

    if (rejected.length) {
      return of(this._rejectMcTraditionalSurveys(rejected, created));
    }

    this._toastService.success(MC_TRADITIONAL_MESSAGES.SAVED, {
      keepOpen: false,
    });

    return this._groupSameCountrySurveys(created).pipe(
      map(() =>
        SurveyCreationMcApiActions.createMcTraditionalSurveysSuccess({
          survey: mcParentSurvey(created),
        }),
      ),
    );
  }

  // A rejected row does not undo the rows that already succeeded — those
  // surveys exist on the server. Legacy abandoned them silently; the created
  // ones ride along on the failure so state can reconcile them later instead
  // of the buyer retrying into a second set of orphan drafts.
  private _rejectMcTraditionalSurveys(
    rejected: readonly McTraditionalSurveyOutcome[],
    created: readonly McCreatedSurvey[],
  ): Action {
    const failedLocales = rejected.map(({ locale }) =>
      localeLabel(locale, this._referenceData.countries()),
    );

    this._toastService.error(
      `${MC_TRADITIONAL_MESSAGES.REJECTED_SURVEYS} ${failedLocales.join(', ')}`,
    );

    return SurveyCreationMcApiActions.createMcTraditionalSurveysFailure({
      failedLocales,
      createdSurveys: created.map(({ survey }) => survey),
    });
  }

  private _groupSameCountrySurveys(
    created: readonly McCreatedSurvey[],
  ): Observable<unknown[]> {
    return of(...created).pipe(
      mergeMap((current) =>
        this._excludeSameCountrySiblings(
          current.survey.id,
          sameCountrySiblingIds(created, current),
        ),
      ),
      toArray(),
    );
  }

  private _excludeSameCountrySiblings(
    surveyId: number,
    siblingIds: readonly number[],
  ): Observable<unknown> {
    if (!siblingIds.length) {
      return of(null);
    }

    return this._surveyService
      .patchSurvey(
        surveyId,
        [{ op: 'replace', path: '/survey_grouping', value: siblingIds }],
        'page',
      )
      .pipe(this.psCatchError(() => of(null), { metadata: { notify: false } }));
  }

  private _withDraftContext<TAction extends Action>(): OperatorFunction<
    TAction,
    [TAction, Result<PreDraftChildDraftsInput, string>]
  > {
    return (trigger$) =>
      trigger$.pipe(
        concatLatestFrom(() => [
          this._store.select(selectAudienceChildren),
          this._store.select(selectCoreChildren),
          this._store.select(selectPricing),
          this._store.select(selectFeasibilityCpi),
          this._store.select(selectInsightsChildren),
          this._store.select(selectQualificationsRecord),
        ]),
        map(
          ([
            action,
            children,
            coreChildren,
            pricingState,
            feasibilityCpi,
            insightsChildren,
            qualificationsRecord,
          ]) => [
            action,
            this._authSelectors.user().match({
              some: (user) =>
                Result.ok<PreDraftChildDraftsInput>({
                  children: Object.entries(children).map(
                    ([key, child]): SurveyChildEntry => [
                      key,
                      child,
                      coreChildren[key] ?? DEFAULT_CORE_CHILD_STATE,
                    ],
                  ),
                  pricing: {
                    ...pricingState,
                    feasibilityCpi,
                  } satisfies SurveyPricingSnapshot,
                  insightsChildren,
                  qualificationsRecord,
                  userId: user.id,
                  companyId: this._authSelectors.activeCompanyId(),
                  tooFewSelectedMessage: MC_MESSAGES.NO_LOCALES_SELECTED,
                  invalidChildMessage: MC_MESSAGES.LOCALE_NOT_SAVEABLE,
                }),
              none: () => Result.err(MC_MESSAGES.NO_SIGNED_IN_USER),
            }),
          ],
        ),
      );
  }

  private _withSelectedDrafts(): OperatorFunction<
    Action,
    PreDraftChildDraft[]
  > {
    return (trigger$) =>
      trigger$.pipe(
        this._withDraftContext(),
        map(([, context]) => context.andThen(buildPreDraftChildDrafts)),
        tap((drafts) =>
          drafts.inspectErr((message) => this._toastService.error(message)),
        ),
        filter((drafts) => drafts.isOk()),
        map((drafts) => drafts.unwrap()),
      );
  }
}
